import { create } from 'zustand';
import type {
  AppStep,
  CaptureMode,
  Gender,
  UnitSystem,
  WornClothingType,
  ReferenceObjectType,
  CalibrationResult,
  DetectionResult,
  BodyMeasurements,
  GarmentMeasurements,
  ProcessingProgress,
  RotationKeyframe,
} from '@/types/measurements';

interface AppState {
  // Flow state
  currentStep: AppStep;
  setStep: (step: AppStep) => void;

  // User info
  userName: string;
  setUserName: (name: string) => void;
  gender: Gender;
  setGender: (gender: Gender) => void;
  userHeightCm: number | null;
  setUserHeightCm: (height: number) => void;

  // Capture settings
  wornClothing: WornClothingType | null;
  setWornClothing: (type: WornClothingType) => void;
  referenceObject: ReferenceObjectType | null;
  setReferenceObject: (type: ReferenceObjectType) => void;

  // Photos
  frontPhoto: string | null; // data URL
  sidePhoto: string | null; // data URL
  setFrontPhoto: (photo: string) => void;
  setSidePhoto: (photo: string | null) => void;

  // Detection results
  frontDetection: DetectionResult | null;
  sideDetection: DetectionResult | null;
  setFrontDetection: (result: DetectionResult) => void;
  setSideDetection: (result: DetectionResult | null) => void;

  // Calibration
  calibration: CalibrationResult | null;
  setCalibration: (result: CalibrationResult) => void;

  // Measurement results
  measurements: BodyMeasurements | null;
  overallConfidence: number;
  setMeasurements: (measurements: BodyMeasurements, confidence: number) => void;

  // Garment selections
  selectedGarments: string[];
  setSelectedGarments: (garments: string[]) => void;
  garmentResults: GarmentMeasurements[];
  setGarmentResults: (results: GarmentMeasurements[]) => void;

  // Processing state
  isProcessing: boolean;
  processingProgress: ProcessingProgress | null;
  error: string | null;
  setProcessing: (isProcessing: boolean, progress?: ProcessingProgress) => void;
  setError: (error: string | null) => void;

  // Capture mode
  captureMode: CaptureMode;
  setCaptureMode: (mode: CaptureMode) => void;
  rotationKeyframes: RotationKeyframe[];
  addRotationKeyframe: (keyframe: RotationKeyframe) => void;
  clearRotationKeyframes: () => void;

  // Display preferences
  unitSystem: UnitSystem;
  setUnitSystem: (unit: UnitSystem) => void;

  // Reset
  reset: () => void;
}

const savedUnit = (typeof localStorage !== 'undefined'
  ? localStorage.getItem('fitafrica-unit')
  : null) as UnitSystem | null;

const initialState = {
  currentStep: 'welcome' as AppStep,
  userName: '',
  gender: 'male' as Gender,
  userHeightCm: null,
  wornClothing: null,
  referenceObject: null,
  frontPhoto: null,
  sidePhoto: null,
  frontDetection: null,
  sideDetection: null,
  calibration: null,
  measurements: null,
  overallConfidence: 0,
  selectedGarments: [] as string[],
  garmentResults: [] as GarmentMeasurements[],
  isProcessing: false,
  processingProgress: null,
  error: null,
  captureMode: 'quick' as CaptureMode,
  rotationKeyframes: [] as RotationKeyframe[],
  unitSystem: savedUnit ?? ('cm' as UnitSystem),
};

export const useAppStore = create<AppState>((set) => ({
  ...initialState,

  setStep: (step) => set({ currentStep: step }),
  setUserName: (name) => set({ userName: name }),
  setGender: (gender) => set({ gender }),
  setUserHeightCm: (height) => set({ userHeightCm: height }),
  setWornClothing: (type) => set({ wornClothing: type }),
  setReferenceObject: (type) => set({ referenceObject: type }),
  setFrontPhoto: (photo) => set({ frontPhoto: photo }),
  setSidePhoto: (photo) => set({ sidePhoto: photo }),
  setFrontDetection: (result) => set({ frontDetection: result }),
  setSideDetection: (result) => set({ sideDetection: result }),
  setCalibration: (result) => set({ calibration: result }),
  setMeasurements: (measurements, confidence) =>
    set({ measurements, overallConfidence: confidence }),
  setSelectedGarments: (garments) => set({ selectedGarments: garments }),
  setGarmentResults: (results) => set({ garmentResults: results }),
  setProcessing: (isProcessing, progress) =>
    set({
      isProcessing,
      processingProgress: progress ?? null,
      error: isProcessing ? null : undefined,
    }),
  setError: (error) => set({ error, isProcessing: false }),

  setCaptureMode: (mode) => set({ captureMode: mode }),
  addRotationKeyframe: (keyframe) =>
    set((state) => ({ rotationKeyframes: [...state.rotationKeyframes, keyframe] })),
  clearRotationKeyframes: () => set({ rotationKeyframes: [] }),

  setUnitSystem: (unit) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('fitafrica-unit', unit);
    set({ unitSystem: unit });
  },

  reset: () => set(initialState),
}));
